import { Alert, Divider, Grid, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import type { DimensionDescriptor, MetricDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";
import type { FieldWithOrder } from "../../../../../hooks/FieldWithOrder.ts";
import useDrillDown from "../../../../../hooks/useDrillDown.ts";
import {
  selectDimensions,
  selectDimensionsStructureForCurrentReportType,
} from "../../../../../store/selectors/metaDataSelectors.ts";
import { selectDrillDownDefaultDimensions } from "../../../../../store/slices/drillDownConfigurationSlice.ts";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types.ts";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";
import { isMetricDimension } from "../../../../../utilities/MetricDimensionNames.ts";
import CollapseContainer from "../../../../common/CollapseContainer.tsx";
import ColumnsOptions from "../columns/ColumnsOptions.tsx";
import ConditionsCollapsedHeader from "../ConditionsCollapsedHeader.tsx";
import ConditionsPanel from "../ConditionsPanel.tsx";
import DrillDownGridWrapper from "../DrillDownGridWrapper.tsx";
import RecordsCounter from "../RecordsCounter.tsx";
import ToolBar from "../ToolBar.tsx";

interface Props {
  configuration: DrillDownPage;
  info: MetricDrillDownInfo;
}

const PmPage = ({ configuration, info }: Props) => {
  const dimensions = useSelector(selectDimensions);
  const dimensionsStructure = useSelector(selectDimensionsStructureForCurrentReportType);
  const defaultDimensions = useSelector(selectDrillDownDefaultDimensions);
  const [columnsInitialized, setColumnsInitialized] = useState<boolean>(false);

  const state = useDrillDown(info);

  const [showColumnsOptions, setShowColumnsOptions] = useState<boolean | undefined>();

  useEffect(() => {
    const fields = getDefaultFields(defaultDimensions, dimensions, info.metricDimension);
    state.actions.changeSelectedFields(fields);
    const entryNoField = fields.find((f) => f.field.name === "EntryNo");
    if (entryNoField) {
      state.actions.changeFieldSorting([{ field: entryNoField.field, sortAsc: true, order: 0 }]);
    }
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setColumnsInitialized(true);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [dimensions]);

  const hideColumnsOptions = useCallback(() => setShowColumnsOptions(false), []);

  return (
    <Grid container sx={{ position: "relative", width: "100%", height: "100%" }}>
      <CollapseContainer
        collapseElement={<ConditionsCollapsedHeader conditionsCount={state.conditions.length} />}
        containerSx={{
          height: "100%",
          ".MuiCollapse-root": {
            height: "100%",
          },
        }}
      >
        <ConditionsPanel state={state} configuration={configuration} info={info} />
      </CollapseContainer>
      <Stack sx={{ flex: 1, width: "unset" }}>
        <Stack spacing={2} useFlexGap sx={{ flex: 1, width: "unset", pl: "1.3rem", py: 2, pr: 3 }}>
          <ToolBar state={state} onShowColumnsOptions={() => setShowColumnsOptions(true)} />
          {state.error && (
            <Alert sx={{ mb: ".5rem" }} severity="error">
              {state.error}
            </Alert>
          )}
          {!columnsInitialized && <InlineLoader text="Preparing data..." />}
          {columnsInitialized && configuration && info.conditions && info.conditions.length > 0 && (
            <DrillDownGridWrapper
              state={state}
              info={info}
              disableCellClick={!!configuration.layer?.measure?.config?.persistedAggregationName}
              highlightedColumns={getActiveLayer(configuration).info?.combined.valueTargetFields}
            />
          )}
        </Stack>
        <Divider />
        <Grid container sx={{ backgroundColor: "background.grey", width: "100%", pr: 3 }}>
          <RecordsCounter totalCount={state.rowsTotal} align="right" />
        </Grid>
      </Stack>
      {showColumnsOptions !== undefined && (
        <ColumnsOptions
          dimensions={dimensions}
          selectedFields={state.selectedFields}
          sortedFields={state.sortedFields}
          groupStructure={dimensionsStructure}
          open={showColumnsOptions === true}
          withAllocation={false}
          onApplySelection={state.actions.changeSelectedFields}
          onApplySorting={state.actions.changeFieldSorting}
          onClose={hideColumnsOptions}
        />
      )}
    </Grid>
  );
};

const getDefaultFields = (defaultDimensions: string[], allFields: DimensionDescriptor[], metricDimension: string) => {
  return defaultDimensions
    .filter((name) => !isMetricDimension(name) || (isMetricDimension(name) && name === metricDimension))
    .map<FieldWithOrder | undefined>((name, index) => {
      const field = allFields.find((f) => f.name === name);
      if (field) {
        return { field, order: index, canBeRemoved: name !== "No" };
      }
      return undefined;
    })
    .filter((item): item is FieldWithOrder => !!item);
};

export default PmPage;
